/**
 * App-level TanStack Table v9 setup.
 * Registers only the features we use so column/cell types stay shared
 * without repeating TFeatures everywhere.
 */
import {
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper as createTanstackColumnHelper,
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  sortFns,
  tableFeatures,
  useTable,
  type Cell as TanStackCell,
  type CellContext as TanStackCellContext,
  type ColumnDef as TanStackColumnDef,
  type Header as TanStackHeader,
  type Row as TanStackRow,
  type RowData,
  type SortDirection,
  type SortingState,
  type TableOptions,
} from '@tanstack/react-table';

export const appTableFeatures = tableFeatures({
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns,
});

export type AppTableFeatures = typeof appTableFeatures;

type AppTableOptions<TData extends RowData> = Omit<TableOptions<AppTableFeatures, TData>, 'features'>;

export function useReactTable<TData extends RowData>(options: AppTableOptions<TData>) {
  return useTable({
    ...options,
    features: appTableFeatures,
  });
}

export function createColumnHelper<TData extends RowData>() {
  return createTanstackColumnHelper<AppTableFeatures, TData>();
}

export { flexRender };
export type { RowData, SortDirection, SortingState };

export type ColumnDef<TData extends RowData, TValue = unknown> = TanStackColumnDef<AppTableFeatures, TData, TValue>;
export type Row<TData extends RowData> = TanStackRow<AppTableFeatures, TData>;
export type Header<TData extends RowData, TValue = unknown> = TanStackHeader<AppTableFeatures, TData, TValue>;
export type Cell<TData extends RowData, TValue = unknown> = TanStackCell<AppTableFeatures, TData, TValue>;
export type CellContext<TData extends RowData, TValue = unknown> = TanStackCellContext<AppTableFeatures, TData, TValue>;
